import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Sparkles } from "lucide-react";

export const InviteDialog = ({ open, onOpenChange, jobId, candidate, onSent, initialTemplate = "friendly" }) => {
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState(initialTemplate);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [customised, setCustomised] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateKey(initialTemplate);
      api.get("/invites/templates").then((r) => setTemplates(r.data)).catch(() => {});
    }
  }, [open, initialTemplate]);

  // Reload preview whenever template or candidate changes (unless user has edited).
  useEffect(() => {
    if (!open || !candidate) return;
    if (customised) return;
    api.post("/invites/preview", {
      job_id: jobId,
      email: candidate.email,
      name: candidate.name,
      template_key: templateKey,
    }).then((r) => {
      setSubject(r.data.subject);
      setBody(r.data.body);
    }).catch(() => {});
  }, [open, templateKey, jobId, candidate, customised]);

  useEffect(() => {
    if (!open) {
      setCustomised(false);
      setTemplateKey(initialTemplate);
    }
  }, [open, initialTemplate]);

  if (!candidate) return null;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/invites", {
        job_id: jobId,
        email: candidate.email,
        name: candidate.name,
        template_key: templateKey,
        subject: customised ? subject : undefined,
        body: customised ? body : undefined,
      });
      toast.success(data.resent ? "Invite resent" : `Invited ${candidate.email}`);
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invite-dialog"
        className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Send className="w-4 h-4 text-[#06B6D4]" /> Invite to apply
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Sending to <span className="text-slate-200">{candidate.email}</span>
            {candidate.name && <span className="text-slate-500"> · {candidate.name}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <div className="mono-label mb-2">template</div>
            <Select value={templateKey} onValueChange={(v) => { setTemplateKey(v); setCustomised(false); }}>
              <SelectTrigger data-testid="invite-template-select" className="bg-[#0B0F19] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 text-slate-100">
                {templates.map((t) => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="mono-label mb-2 flex items-center justify-between">
              <span>subject</span>
              {customised && <span className="text-[#F59E0B] text-[10px]">EDITED</span>}
            </div>
            <Input
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setCustomised(true); }}
              data-testid="invite-subject-input"
              className="bg-[#0B0F19] border-white/10"
            />
          </div>

          <div>
            <div className="mono-label mb-2 flex items-center justify-between">
              <span>message</span>
              {customised && <span className="text-[#F59E0B] text-[10px]">EDITED</span>}
            </div>
            <Textarea
              value={body}
              onChange={(e) => { setBody(e.target.value); setCustomised(true); }}
              data-testid="invite-body-input"
              rows={10}
              className="bg-[#0B0F19] border-white/10 font-mono text-xs leading-relaxed"
            />
            <div className="text-[10px] text-slate-500 font-mono mt-1">
              Placeholders (already filled): {"{candidate_name} · {recruiter_name} · {company} · {job_title}"}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
          <button
            type="button"
            onClick={() => { setCustomised(false); }}
            disabled={!customised}
            className="text-xs text-slate-400 hover:text-white disabled:opacity-40 flex items-center gap-1"
            data-testid="invite-reset-btn"
          >
            <Sparkles className="w-3 h-3" /> Reset to template
          </button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={send}
              disabled={sending || !subject.trim() || !body.trim()}
              data-testid="invite-send-btn"
              className="bg-[#06B6D4] hover:bg-[#0891B2] text-[#0B0F19] rounded-full font-medium"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {sending ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
